import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import { FaChevronRight } from "react-icons/fa";
import { Z_INDEX } from "@/utils/z-index";

// Menu Context for nested menus
interface MenuContextType {
  closeMenu: () => void;
  size: "sm" | "md" | "lg";
}

const MenuContext = createContext<MenuContextType | null>(null);

export interface MenuProps {
  /** Trigger element (optional - use with trigger-based API) */
  trigger?: React.ReactElement;
  /** Menu items */
  children: React.ReactNode;
  /** Open state (controlled) */
  open?: boolean;
  /** Callback when menu opens/closes */
  onOpenChange?: (open: boolean) => void;
  /** Callback when menu closes (MUI-style API) */
  onClose?: () => void;
  /** Anchor element (MUI-style API) */
  anchorEl?: HTMLElement | null;
  /** Placement relative to trigger */
  placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
  /** Offset from trigger [x, y] */
  offset?: [number, number];
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Disable portal */
  disablePortal?: boolean;
  /** Custom class name */
  className?: string;
  /** Close on item click */
  closeOnSelect?: boolean;
}

export interface MenuItemProps {
  /** Item content */
  children: React.ReactNode;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Danger/destructive item */
  danger?: boolean;
  /** Leading icon */
  icon?: React.ReactNode;
  /** Trailing content (e.g., shortcut) */
  trailing?: React.ReactNode;
  /** Submenu items */
  submenu?: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Selected state */
  selected?: boolean;
  /** Value for Select compatibility */
  value?: string | number;
}

export interface MenuDividerProps {
  className?: string;
}

export interface MenuLabelProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Arc-style Menu component
 *
 * Supports two APIs:
 * 1. Trigger-based: <Menu trigger={<Button>Open</Button>}>...</Menu>
 * 2. Anchor-based (MUI-style): <Menu open={open} onClose={handleClose} anchorEl={buttonRef.current}>...</Menu>
 *
 * Features:
 * - Dropdown menu with trigger
 * - Nested submenus
 * - Keyboard navigation
 * - Icons and shortcuts
 * - Dividers and labels
 */
const Menu = forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      trigger,
      children,
      open: controlledOpen,
      onOpenChange,
      onClose,
      anchorEl,
      placement = "bottom-start",
      offset = [0, 4],
      size = "md",
      disablePortal = false,
      className = "",
      closeOnSelect = true,
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Determine if using trigger-based or anchor-based API
    const useTriggerApi = trigger !== undefined;
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    // Get the anchor element (either from trigger ref or anchorEl prop)
    const getAnchorElement = useCallback(() => {
      if (useTriggerApi) {
        return triggerRef.current;
      }
      return anchorEl;
    }, [useTriggerApi, anchorEl]);

    const setOpen = useCallback(
      (value: boolean) => {
        if (!isControlled) {
          setInternalOpen(value);
        }
        onOpenChange?.(value);
        if (!value) {
          onClose?.();
        }
      },
      [isControlled, onOpenChange, onClose]
    );

    const closeMenu = useCallback(() => {
      setOpen(false);
      setHighlightedIndex(-1);
    }, [setOpen]);

    // Calculate position
    const updatePosition = useCallback(() => {
      const anchor = getAnchorElement();
      if (!anchor || !menuRef.current) return;

      const anchorRect = anchor.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      // Vertical position
      if (placement.startsWith("bottom")) {
        top = anchorRect.bottom + offset[1];
        // Flip to top if not enough space
        if (top + menuRect.height > viewportHeight && anchorRect.top > menuRect.height) {
          top = anchorRect.top - menuRect.height - offset[1];
        }
      } else {
        top = anchorRect.top - menuRect.height - offset[1];
        // Flip to bottom if not enough space
        if (top < 0 && viewportHeight - anchorRect.bottom > menuRect.height) {
          top = anchorRect.bottom + offset[1];
        }
      }

      // Horizontal position
      if (placement.endsWith("-start")) {
        left = anchorRect.left + offset[0];
      } else {
        left = anchorRect.right - menuRect.width + offset[0];
      }

      // Constrain to viewport
      left = Math.max(8, Math.min(left, viewportWidth - menuRect.width - 8));
      top = Math.max(8, Math.min(top, viewportHeight - menuRect.height - 8));

      setPosition({ top, left });
    }, [placement, offset, getAnchorElement]);

    // Handle trigger click
    const handleTriggerClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(!isOpen);
      },
      [isOpen, setOpen]
    );

    // Update position when open
    useEffect(() => {
      if (isOpen) {
        requestAnimationFrame(updatePosition);
      }
    }, [isOpen, updatePosition]);

    // Handle click outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        const anchor = getAnchorElement();
        if (
          menuRef.current &&
          !menuRef.current.contains(e.target as Node) &&
          anchor &&
          !anchor.contains(e.target as Node)
        ) {
          closeMenu();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, closeMenu, getAnchorElement]);

    // Handle escape key
    useEffect(() => {
      if (!isOpen) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          closeMenu();
          const anchor = getAnchorElement();
          if (anchor && 'focus' in anchor) {
            (anchor as HTMLElement).focus();
          }
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, closeMenu, getAnchorElement]);

    // Update position on scroll/resize
    useEffect(() => {
      if (!isOpen) return;

      const handleUpdate = () => updatePosition();
      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
      };
    }, [isOpen, updatePosition]);

    // Clone trigger with ref and click handler (only for trigger-based API)
    const triggerElement = useTriggerApi && trigger ? React.cloneElement(trigger as React.ReactElement<any>, {
      ref: triggerRef,
      onClick: (e: React.MouseEvent) => {
        handleTriggerClick(e);
        (trigger as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props.onClick?.(e);
      },
      "aria-haspopup": "menu" as const,
      "aria-expanded": isOpen,
    }) : null;

    const menuContent = isOpen && (
      <MenuContext.Provider value={{ closeMenu: closeOnSelect ? closeMenu : () => {}, size }}>
        <div
          ref={(node) => {
            menuRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="menu"
          className={`
            bg-surface-elevated
            border border-arc-border
            rounded-arc-md
            shadow-arc-lg
            py-1
            min-w-[160px]
            animate-arc-fade-in
            ${className}
          `}
          style={{
            position: disablePortal ? "absolute" : "fixed",
            top: position.top,
            left: position.left,
            zIndex: Z_INDEX.POPOVER_IN_MODAL,
          }}
        >
          {children}
        </div>
      </MenuContext.Provider>
    );

    // For anchor-based API, just render the menu content
    if (!useTriggerApi) {
      return disablePortal
        ? menuContent
        : typeof window !== "undefined" && menuContent
          ? createPortal(menuContent, document.body)
          : null;
    }

    // For trigger-based API, render trigger + menu
    return (
      <>
        {triggerElement}
        {disablePortal
          ? menuContent
          : typeof window !== "undefined" && menuContent
            ? createPortal(menuContent, document.body)
            : null}
      </>
    );
  }
);

Menu.displayName = "Menu";

/**
 * Menu Item component
 */
const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      children,
      onClick,
      disabled = false,
      danger = false,
      icon,
      trailing,
      submenu,
      className = "",
      selected = false,
    },
    ref
  ) => {
    const context = useContext(MenuContext);
    const [showSubmenu, setShowSubmenu] = useState(false);
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
    const itemRef = useRef<HTMLDivElement>(null);
    const submenuRef = useRef<HTMLDivElement>(null);

    const size = context?.size || "md";

    const sizeStyles = {
      sm: "px-2 py-1 text-xs",
      md: "px-3 py-2 text-sm",
      lg: "px-4 py-2.5 text-base",
    };

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) return;
        if (submenu) {
          setShowSubmenu(!showSubmenu);
          return;
        }
        onClick?.(e);
        context?.closeMenu();
      },
      [disabled, submenu, showSubmenu, onClick, context]
    );

    // Calculate submenu position
    const updateSubmenuPosition = useCallback(() => {
      if (!itemRef.current || !submenuRef.current) return;

      const itemRect = itemRef.current.getBoundingClientRect();
      const submenuRect = submenuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      let left = itemRect.right + 4;
      // Flip to left if not enough space
      if (left + submenuRect.width > viewportWidth) {
        left = itemRect.left - submenuRect.width - 4;
      }

      setSubmenuPosition({
        top: itemRect.top,
        left,
      });
    }, []);

    useEffect(() => {
      if (showSubmenu) {
        requestAnimationFrame(updateSubmenuPosition);
      }
    }, [showSubmenu, updateSubmenuPosition]);

    return (
      <>
        <div
          ref={(node) => {
            itemRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="menuitem"
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onMouseEnter={() => submenu && setShowSubmenu(true)}
          onMouseLeave={() => submenu && setShowSubmenu(false)}
          className={`
            flex items-center gap-2
            cursor-pointer
            transition-colors duration-arc-fast
            rounded-arc-sm
            mx-1
            ${sizeStyles[size]}
            ${
              disabled
                ? "opacity-50 cursor-not-allowed"
                : danger
                  ? "text-error hover:bg-error-light/20"
                  : selected
                    ? "bg-accent-pressed text-accent font-medium"
                    : "text-content-primary hover:bg-accent-hover"
            }
            ${className}
          `}
        >
          {icon && (
            <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
              {icon}
            </span>
          )}
          <span className="flex-1">{children}</span>
          {trailing && (
            <span className="flex-shrink-0 text-content-tertiary text-xs">
              {trailing}
            </span>
          )}
          {submenu && (
            <FaChevronRight className="flex-shrink-0 w-3 h-3 text-content-tertiary" />
          )}
        </div>

        {submenu && showSubmenu && (
          <div
            ref={submenuRef}
            className="fixed bg-surface-elevated border border-arc-border rounded-arc-md shadow-arc-lg py-1 min-w-[160px] animate-arc-fade-in"
            style={{
              top: submenuPosition.top,
              left: submenuPosition.left,
              zIndex: Z_INDEX.POPOVER_IN_MODAL + 1,
            }}
            onMouseEnter={() => setShowSubmenu(true)}
            onMouseLeave={() => setShowSubmenu(false)}
          >
            {submenu}
          </div>
        )}
      </>
    );
  }
);

MenuItem.displayName = "MenuItem";

/**
 * Menu Divider component
 */
const MenuDivider: React.FC<MenuDividerProps> = ({ className = "" }) => (
  <div className={`my-1 h-px bg-arc-border ${className}`} role="separator" />
);

MenuDivider.displayName = "MenuDivider";

/**
 * Menu Label component
 */
const MenuLabel: React.FC<MenuLabelProps> = ({ children, className = "" }) => (
  <div
    className={`px-3 py-1.5 text-xs font-semibold text-content-tertiary uppercase tracking-wider ${className}`}
  >
    {children}
  </div>
);

MenuLabel.displayName = "MenuLabel";

export { Menu, MenuItem, MenuDivider, MenuLabel };
export default Menu;
